import { FC, Fragment, useCallback, useState, useEffect } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import SV from 'server-vars';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import {
    getIsOptinPromptShown,
    getSessionPdpItemSaved,
    setSessionPdpItemSaved,
} from '../../PdpSharedOptInPromptModal/pdpSharedOptInPromptModaHelpers';
import { usePdpSharedTracking } from '../../hooks/usePdpSharedTracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { PdpRespSharedItemsRefetchCarouselConfig } from '../../respShared/PdpRespSharedItemsRefetchCarousel/PdpRespSharedItemsRefetchCarouselConfig';
import { AUCTION_SIDERAIL_REDESIGN, SOLD_PDP_V1 } from '../../../constants/abTestsConstants';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { setDwelledOnPdp } from 'dibs-buyer-layout/exports/dwelledOnPdpHelpers';
import {
    useBuyerIncentivesV3ClientVariants,
    useTrackBuyerIncentivesV3,
} from 'dibs-buyer-incentives/exports/buyerIncentivesV3Helpers';
import { locations } from '../../../utils/tracking/shared/favoritesTracking';
import { youMayAlsoLike } from '../../../shared/sharedMessages';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { PdpRespUnavailableHeader } from '../PdpRespUnavailableHeader/PdpRespUnavailableHeader';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { PdpRespUnavailableHeaderYMAL } from '../PdpRespUnavailableHeaderYMAL/PdpRespUnavailableHeaderYMAL';
import { PdpSharedAuctionEndedLink } from '../../shared/PdpSharedAuctionEndedLink/PdpSharedAuctionEndedLink';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs';
import { PdpRespUnavailableDetailsTabs } from '../PdpRespUnavailableDetailsTabs/PdpRespUnavailableDetailsTabs';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';
import { PdpRespSharedBuyerAssurance } from '../../respShared/PdpRespSharedBuyerAssurance/PdpRespSharedBuyerAssurance';
import { PdpRespSharedSellerChatLoader } from '../../respShared/PdpRespSharedSellerChatLoader/PdpRespSharedSellerChatLoader';
import { PdpRespSharedItemsRefetchCarousel } from '../../respShared/PdpRespSharedItemsRefetchCarousel/PdpRespSharedItemsRefetchCarousel';
import { PdpRespSimilarAuctionItemsLazy } from '../../PdpRespSimilarAuctionItems/PdpRespSimilarAuctionItemsLazy';
import { PdpRespSharedPrintDetailsLazy } from '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy';
import { PdpRespSimilarItemsGrid } from '../../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import { PdpSharedAuctionClosed } from '../../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { BrowserHistoryContextProvider } from '../../../global/BrowserHistoryContext/BrowserHistoryContext';
import { PdpSharedUnavailableListingBanner } from '../../PdpSharedUnavailableListingBanner/PdpSharedUnavailableListingBanner';
// styles
import styles from './PdpRespUnavailableContainer.scss';

// types
import { PdpRespUnavailableContainer_item$key as Item } from './__generated__/PdpRespUnavailableContainer_item.graphql';
import { PdpRespUnavailableContainer_user$key as User } from './__generated__/PdpRespUnavailableContainer_user.graphql';
import { PdpRespUnavailableContainer_viewer$key as Viewer } from './__generated__/PdpRespUnavailableContainer_viewer.graphql';

// lazy components
const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites'
        )
);

const PdpRespSharedHeroCarouselPrint = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedHeroCarouselPrint/PdpRespSharedHeroCarouselPrint'
        )
);

const PdpSharedOptInPromptModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../PdpSharedOptInPromptModal/PdpSharedOptInPromptModal'
        )
);

const PdpRespSharedSidebarSellerInfoAndContact = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedSidebarSellerInfoAndContact" */ '../../PdpRespSidebarSellerInfo/PdpRespSharedSidebarSellerInfoAndContact'
        )
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableContainer_viewer on Viewer {
        pdpOptinPromptFF: featureFlag(feature: "pdpOptinPrompt")
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        ...usePdpSharedTracking_viewer
        ...SharedRespLinksBlock_viewer
        ...PdpRespUnavailableHeaderYMAL_viewer
        ...PdpRespUnavailableDetailsTabs_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer @skip(if: $isSoldPDPV1Variant)
        ...PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer
        ...PdpRespSharedSellerChatLoader_viewer @include(if: $fetchChat)
        ...PdpRespSimilarAuctionItemsLazy_viewer
        ...PdpRespSimilarItemsGrid_viewer @include(if: $isSoldPDPV1Variant)
        similarItemsGrid: moreLikeThisItem(first: 10, seller: EXCLUDE, itemId: $itemId)
            @include(if: $isSoldPDPV1Variant) {
            edges {
                node {
                    serviceId
                    ...PdpRespSimilarItemsGrid_similarItems
                }
            }
        }
    }
`;
const itemFragment = graphql`
    fragment PdpRespUnavailableContainer_item on Item {
        serviceId
        isAuctionEnabled
        isSold
        seller {
            serviceId
            isAccessible
            ...PdpRespSharedSidebarSellerInfoAndContact_seller @include(if: $afterDisplay)
        }
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        ...PdpRespSharedTitle_item
        ...usePdpSharedTracking_item
        ...SharedRespLinksBlock_item
        ...PdpRespUnavailableHeader_item
        ...PdpRespSharedBreadcrumbs_item
        ...PdpRespUnavailableDetailsTabs_item
        ...PdpRespSharedHeroCarousel_item
        ...PdpSharedEditorialArticles_refactor_item
        ...PdpRespSharedPrintDetailsLazy_item
        ...PdpRespSimilarItemsGrid_item @include(if: $isSoldPDPV1Variant)
        ...PdpSharedAuctionClosed_item @include(if: $afterDisplay)
        ...PriceDisplay_item
            @include(if: $afterDisplay)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
        ...PdpRespSharedHeroCarouselPrint_item @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_item @include(if: $afterDisplay)
    }
`;
const userFragment = graphql`
    fragment PdpRespUnavailableContainer_user on User {
        serviceId
        ...PdpRespUnavailableHeader_user
        ...PdpRespSharedBuyerAssurance_user
        ...PdpRespUnavailableDetailsTabs_user
        ...PdpRespSharedHeroCarousel_user
        ...PdpRespSharedItemsRefetchCarousel_user
        ...PdpRespSharedPrintDetailsLazy_user
        ...PdpRespSimilarItemsGrid_user @include(if: $isSoldPDPV1Variant)
        ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_user @include(if: $afterDisplay)
        ...PdpRespSimilarAuctionItemsLazy_user
    }
`;

const isAuctionModuleDlosPDPVariant = !!SV.get('isAuctionModuleDlosPDPVariant');

export const PdpRespUnavailableContainer: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const isBuyerIncentivesClientVariant = useBuyerIncentivesV3ClientVariants({ isMobile: false });
    const { isAuctionSiderailRedesignVariant, isSoldPDPV1Variant } = useServerVarsContext();
    const {
        currency,
        relayVars: { afterDisplay, fetchChat },
    } = useRespUnavailableContext();
    const [showOptinPrompt, setShowOptinPrompt] = useState(false);

    usePdpSharedTracking({ item, viewer });

    const { serviceId: userId } = user || {};
    const { pdpOptinPromptFF, isWordpressArticlesEnabled, similarItemsGrid } = viewer;
    const { seller, serviceId: itemId, internalBuyLinks, isAuctionEnabled, isSold } = item;

    const onFavorited = useCallback(() => {
        if (!pdpOptinPromptFF) {
            return;
        }
        const isOptinPromptShown = getIsOptinPromptShown();
        const isPdpItemSaved = getSessionPdpItemSaved();
        const shouldShowOptinPrompt = !!userId && !isOptinPromptShown && !isPdpItemSaved;

        if (shouldShowOptinPrompt) {
            setSessionPdpItemSaved();
            setShowOptinPrompt(true);
        }
    }, [userId, pdpOptinPromptFF]);

    useTrackBuyerIncentivesV3({ isMobile: false });
    useEffect(() => {
        if (isAuctionEnabled) {
            trackAbTestV2Variant(AUCTION_SIDERAIL_REDESIGN);
        }
    }, [isAuctionEnabled, isSold]);

    useEffect(() => {
        if (!isAuctionEnabled) {
            trackAbTestV2Variant(SOLD_PDP_V1);
        }
    }, [isAuctionEnabled]);

    useEffect(() => {
        const timeoutId = setDwelledOnPdp();
        return () => clearTimeout(timeoutId);
    }, []);

    const similarItems = flattenConnectionNodes(similarItemsGrid);
    const shouldRenderAuctionsSidebarVariant = isAuctionEnabled && isAuctionSiderailRedesignVariant;

    return (
        <>
            <div className={styles.printHide}>
                {!isSoldPDPV1Variant && (
                    <div className={styles.topModuleContainer}>
                        <div className={styles.topModuleContainerInner}>
                            <PdpRespUnavailableHeader item={item} user={user} />
                            <div className={styles.divider} />
                            <PdpRespUnavailableHeaderYMAL viewer={viewer} />
                        </div>
                    </div>
                )}
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={`${styles.topContainer} ${styles.carouselContainer} colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1`}
                >
                    <div className={styles.printHide}>
                        <BrowserHistoryContextProvider>
                            <PdpRespSharedHeroCarousel
                                viewer={viewer}
                                item={item}
                                user={user}
                                currency={currency}
                                afterDisplay={afterDisplay}
                                size="small"
                            />
                        </BrowserHistoryContextProvider>
                    </div>
                    <ClientSuspense fallback={null}>
                        <div className={styles.printShow}>
                            <PdpRespSharedHeroCarouselPrint item={item} />
                        </div>
                    </ClientSuspense>
                </div>
                <div className={`${styles.topContainer} colXs12 colLg4 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        {isAuctionEnabled && !shouldRenderAuctionsSidebarVariant && (
                            <PdpSharedAuctionEndedLink />
                        )}
                        {!isAuctionEnabled && isSoldPDPV1Variant && (
                            <PdpSharedUnavailableListingBanner />
                        )}
                        <div className="rowFlex">
                            <div className="colXs12 colLg10">
                                <PdpRespSharedTitle item={item} />
                                {!shouldRenderAuctionsSidebarVariant && (
                                    <div className={styles.printHide}>
                                        <ClientSuspense fallback={null}>
                                            <PriceDisplay
                                                sku={null}
                                                useSvgIcon
                                                item={item}
                                                displayVatLabel
                                                currency={currency}
                                            />
                                        </ClientSuspense>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`${styles.printHide} ${styles.favoritesContainer} colXs2`}
                            >
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        onFavorited={onFavorited}
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                    />
                                </ClientSuspense>
                            </div>
                        </div>
                        {shouldRenderAuctionsSidebarVariant && (
                            <ClientSuspense fallback={null}>
                                <PdpSharedAuctionClosed
                                    item={item}
                                    currency={currency}
                                    isAuctionSiderailRedesignVariant={
                                        isAuctionSiderailRedesignVariant
                                    }
                                />
                            </ClientSuspense>
                        )}

                        {seller?.isAccessible && (
                            <ClientSuspense fallback={null}>
                                <div className={`${styles.sidebarSection} ${styles.printHide}`}>
                                    <PdpRespSharedSidebarSellerInfoAndContact
                                        template="UNAVAILABLE"
                                        user={user}
                                        item={item}
                                        seller={item.seller}
                                        currency={currency}
                                    />
                                </div>
                            </ClientSuspense>
                        )}
                        <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                    </div>
                </div>
            </div>

            <div className={styles.printHide}>
                <PdpRespUnavailableDetailsTabs user={user} item={item} viewer={viewer} />
            </div>
            <div className={styles.printShow}>
                <PdpRespSharedPrintDetailsLazy item={item} user={user} currency={currency} />
            </div>

            {fetchChat && <PdpRespSharedSellerChatLoader viewer={viewer} currency={currency} />}

            <div className={styles.printHide}>
                {PdpRespSharedItemsRefetchCarouselConfig.map(
                    ({ query, title, type, key, getTableMetadata, dataTn }) => {
                        const { isVisible, viewAllLinkData, trackingList, favoritesLocation } =
                            getTableMetadata({
                                seller,
                                itemId,
                            });

                        if (!isVisible) {
                            return null;
                        }

                        return (
                            <Fragment key={key}>
                                {type !== 'similar-recommendation-grid' && (
                                    <PdpRespSharedItemsRefetchCarousel
                                        user={user}
                                        title={title}
                                        dataKey={key}
                                        viewer={viewer}
                                        viewerQuery={query}
                                        currency={currency}
                                        trackingList={trackingList}
                                        disableFavorites={!afterDisplay}
                                        viewAllLinkData={viewAllLinkData}
                                        favoritesLocation={favoritesLocation}
                                        dataTn={dataTn}
                                    />
                                )}
                                {type === 'similar-recommendation-grid' && !isSoldPDPV1Variant && (
                                    <PdpRespSharedItemsRefetchCarousel
                                        user={user}
                                        title={title}
                                        dataKey={key}
                                        viewer={viewer}
                                        viewerQuery={query}
                                        currency={currency}
                                        trackingList={trackingList}
                                        disableFavorites={!afterDisplay}
                                        viewAllLinkData={viewAllLinkData}
                                        favoritesLocation={favoritesLocation}
                                        dataTn={dataTn}
                                    />
                                )}
                                {type === 'more-from-seller-grid' &&
                                    isSoldPDPV1Variant &&
                                    similarItemsGrid?.edges?.length && (
                                        <>
                                            <div className={styles.ymalHeaderContainer}>
                                                <span className={styles.ymalHeader}>
                                                    {youMayAlsoLike}
                                                </span>
                                                <SeoLink
                                                    className={styles.ymalLink}
                                                    linkData={{
                                                        path: `/similar/?item=${item.serviceId}`,
                                                    }}
                                                    target="_blank"
                                                    dataTn="pdp-product-tile-more-from-seller-view-all-link"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="View All"
                                                        id="PdpRespUnavailableContainer.viewAll"
                                                    />
                                                </SeoLink>
                                            </div>
                                            <PdpRespSimilarItemsGrid
                                                gridWrapperClass={styles.ymalContainer}
                                                gridItemWrapperClass={styles.similarItems}
                                                relayVariables={{ isClient: afterDisplay }}
                                                viewer={viewer}
                                                currency={currency}
                                                user={user}
                                                item={item}
                                                similarItems={similarItems}
                                                favoritesLocation={locations.SIMILAR_ITEM_GRID}
                                                gridLocation={locations.SIMILAR_ITEM_GRID}
                                                dataTn="pdp-similar-recommendation-grid"
                                                isDark={false}
                                                showLightBox={false}
                                                showMeasurements={false}
                                                hideBar
                                            />
                                        </>
                                    )}
                            </Fragment>
                        );
                    }
                )}

                {isAuctionModuleDlosPDPVariant && (
                    <PdpRespSimilarAuctionItemsLazy
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        trackingLocation="sold unavailable pdp auction you may also like"
                    />
                )}

                <RecentlyViewedItemsProvider excludedItemId={item.serviceId}>
                    <RecentlyViewedItemsLazy
                        itemsPerPage={5}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        wrapperClassName={classnames(styles.printHide, styles.recentlyViewed, {
                            [styles.divider]: viewer.isWordpressArticlesEnabled,
                        })}
                    />
                </RecentlyViewedItemsProvider>
            </div>

            {isWordpressArticlesEnabled && (
                <div className={styles.printHide}>
                    <PdpSharedEditorialArticles item={item} />
                </div>
            )}

            {isBuyerIncentivesClientVariant === false && (
                <div className={styles.printHide}>
                    <PdpRespSharedBuyerAssurance user={user} />
                </div>
            )}

            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>

            {showOptinPrompt && (
                <ClientSuspense fallback="">
                    <PdpSharedOptInPromptModal
                        isAuctionEnabled={false}
                        onModalClose={() => setShowOptinPrompt(false)}
                    />
                </ClientSuspense>
            )}
        </>
    );
};
